import type {
  Address, AffiliateSummary, Banner, Category, CartLine, Coupon, LoyaltyAccount, LoyaltyTransaction, Order,
  Page, PlaceOrderInput, Product, ProductQuery, Review, Tenant, Testimonial, User, Wallet, WalletTransaction,
} from '@/types';

/**
 * Service contracts. The UI depends ONLY on these interfaces (via `services/index.ts`).
 * `services/mock/*` implements them in-memory; a future `services/http/*` will call the real REST API
 * without any UI change. Every method is async and may reject with `ServiceError`.
 */
export class ServiceError extends Error {
  constructor(message: string, public code: 'NOT_FOUND' | 'INVALID' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'FEATURE_DISABLED' | 'CONFLICT' | 'UNKNOWN' = 'UNKNOWN') {
    super(message);
  }
}

export interface TenantService {
  getCurrent(): Promise<Tenant>;
}

export interface CatalogService {
  listCategories(): Promise<Category[]>;
  getCategory(slug: string): Promise<Category>;
  listProducts(query?: ProductQuery): Promise<Page<Product>>;
  getProduct(slug: string): Promise<Product>;
  getProductsByIds(ids: string[]): Promise<Product[]>;
  getRelated(productId: string, limit?: number): Promise<Product[]>;
  listReviews(productId: string): Promise<Review[]>;
  suggest(q: string): Promise<Product[]>;
}

export interface MarketingService {
  listBanners(): Promise<Banner[]>;
  listTestimonials(): Promise<Testimonial[]>;
  submitContact(input: { name: string; email: string; message: string }): Promise<{ ticketId: string }>;
}

export type CouponCheck =
  | { valid: true; coupon: Coupon; discount: number }
  | { valid: false; reason: string };

export interface CouponService {
  listPublic(): Promise<Coupon[]>;
  listMine(): Promise<{ available: Coupon[]; inactive: { coupon: Coupon; reason: string }[] }>;
  validate(code: string, lines: CartLine[]): Promise<CouponCheck>;
}

export interface AuthService {
  currentUser(): Promise<User | null>;
  login(input: { email: string; password: string }): Promise<User>;
  register(input: { name: string; email: string; password: string; referralCode?: string }): Promise<User>;
  logout(): Promise<void>;
  updateProfile(input: Partial<Pick<User, 'name' | 'email' | 'phone'>>): Promise<User>;
  listAddresses(): Promise<Address[]>;
  saveAddress(a: Omit<Address, 'id'> & { id?: string }): Promise<Address>;
  removeAddress(id: string): Promise<void>;
}

export interface OrderService {
  list(): Promise<Order[]>;
  get(id: string): Promise<Order>;
  place(input: PlaceOrderInput): Promise<Order>;
  placeGuest(input: { name: string; email: string; phone: string; address: { line1: string; city: string; state: string; pincode: string }; lines: { productId: string; quantity: number }[]; paymentMethod: PlaceOrderInput['paymentMethod'] }): Promise<Order>;
  cancel(id: string): Promise<Order>;
  requestReturn(id: string, reason: string): Promise<void>;
}

export interface WalletService {
  get(): Promise<Wallet>;
  transactions(): Promise<WalletTransaction[]>;
  /** MOCK ONLY: simulates a payment-gateway top-up. Real impl redirects to the gateway and credits on webhook. */
  topUp(amount: number): Promise<Wallet>;
}

export interface LoyaltyService {
  get(): Promise<{ account: LoyaltyAccount; transactions: LoyaltyTransaction[] }>;
  redeemReward(rewardId: string): Promise<LoyaltyAccount>;
}

export interface AffiliateService {
  summary(): Promise<AffiliateSummary>;
}

export interface WishlistService {
  list(): Promise<Product[]>;
  listIds(): Promise<string[]>;
  toggle(productId: string): Promise<{ inWishlist: boolean; productId: string }>;
}

export interface Services {
  tenant: TenantService;
  catalog: CatalogService;
  marketing: MarketingService;
  coupons: CouponService;
  auth: AuthService;
  orders: OrderService;
  wallet: WalletService;
  loyalty: LoyaltyService;
  affiliate: AffiliateService;
  wishlist: WishlistService;
}
